import React, { useId } from 'react';
import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/fluent-adapter';
import { Checkbox, Label, Textarea, RadioGroup } from '@/fluent-adapter';
import { RequiredIndicator } from '@/components/Accessibility/RequiredIndicator';
import { FIELD_LIMITS } from '../../constants/fieldLimits';

interface NatureOfCrimeForm {
  isTerrorism?: boolean;
  isChildExploitation?: boolean;
  isHumanTrafficking?: boolean;
  isMissingPerson?: boolean;
  isCorruption?: boolean;
  isCybercrime?: boolean;
  isDefamation?: boolean;
  isHateSpeech?: boolean;
  isMoneyLaundering?: boolean;
  isHarassment?: boolean;
  isTheft?: boolean;
  isViolentCrime?: boolean;
  isAdultSexualAbuseOrExploitation?: boolean;
  isDrugsOrDrugTrafficking?: boolean;
  isFraudWithMoneyLaundering?: boolean;
  isFraudNoMoneyLaundering?: boolean;
  isOther?: boolean;
  isOtherDescription?: string;
}

interface NatureOfCrimeStepProps {
  formData: NatureOfCrimeForm;
  onFormChange: (data: Partial<NatureOfCrimeForm>) => void;
  /** Whether the request is child-exploitation-related. Card moved from request-type step. */
  cpConcern: boolean | null;
  onCpConcernChange: (val: boolean) => void;
  errors?: Record<string, string>;
}

// Crime types with display names - keys match the Redux slice property names
const CRIME_TYPE_OPTIONS: { key: keyof NatureOfCrimeForm; label: string; description: string }[] = [
  { key: 'isTerrorism', label: 'Terrorism', description: 'Terrorist activities, threats' },
  { key: 'isHumanTrafficking', label: 'Human Trafficking', description: 'Human trafficking crimes' },
  { key: 'isMissingPerson', label: 'Missing person', description: 'Missing person cases' },
  { key: 'isCorruption', label: 'Corruption', description: 'Public corruption, bribery' },
  { key: 'isCybercrime', label: 'Cybercrime', description: 'Computer crimes, hacking' },
  { key: 'isDefamation', label: 'Defamation', description: 'Defamation, libel, slander' },
  { key: 'isHateSpeech', label: 'Hate Speech', description: 'Hate speech, discrimination' },
  { key: 'isMoneyLaundering', label: 'Money laundering', description: 'Financial crimes, laundering' },
  { key: 'isHarassment', label: 'Harassment/Personal Safety', description: 'Online harassment, cyberbullying' },
  { key: 'isTheft', label: 'Theft', description: 'Theft, robbery, burglary' },
  { key: 'isViolentCrime', label: 'Violent Crime/Crime Against Person', description: 'Assault, murder, violent offenses' },
  { key: 'isAdultSexualAbuseOrExploitation', label: 'Adult Sexual Abuse/Exploitation', description: 'Adult sexual crimes' },
  { key: 'isDrugsOrDrugTrafficking', label: 'Drugs/Drug Trafficking', description: 'Drug-related crimes' },
  { key: 'isFraudWithMoneyLaundering', label: 'Fraud/Financial crime (with Money Laundering)', description: 'Fraud involving money laundering' },
  { key: 'isFraudNoMoneyLaundering', label: 'Fraud/Financial crime (no Money Laundering)', description: 'Fraud without money laundering' },
  { key: 'isOther', label: 'Other', description: 'Other criminal activities not listed' },
];

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', rowGap: '16px' },
  header: { display: 'flex', flexDirection: 'column', rowGap: '4px' },
  title: { fontSize: '20px', fontWeight: 600 },
  muted: { color: tokens.colorNeutralForeground3 },
  grid: { display: 'grid', gridTemplateColumns: '1fr', rowGap: '8px', columnGap: '8px', '@media (min-width: 768px)': { gridTemplateColumns: '1fr 1fr' } },
  option: { display: 'flex', alignItems: 'flex-start', columnGap: '8px', ...shorthands.padding('8px'), borderRadius: tokens.borderRadiusMedium, border: `1px solid ${tokens.colorNeutralStroke2}`, ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover } },
  optionContent: { flex: 1 },
  optionLabel: { fontWeight: 600, cursor: 'pointer' },
  hint: { fontSize: '12px', color: tokens.colorNeutralForeground3 },
  textareaContainer: { display: 'flex', flexDirection: 'column', rowGap: '8px' },
  errors: { color: tokens.colorStatusDangerForeground1 },
  right: { textAlign: 'right' },
  required: {
    color: tokens.colorStatusDangerForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
});

export const NatureOfCrimeStep: React.FC<NatureOfCrimeStepProps> = ({
  formData,
  onFormChange,
  cpConcern,
  onCpConcernChange,
  errors = {},
}) => {
  const styles = useStyles();
  const headingId = useId();
  const headingDescId = useId();
  const cpConcernLabelId = useId();
  const cpConcernDescId = useId();
  const handleCrimeTypeChange = (key: keyof NatureOfCrimeForm, checked: boolean) => {
    onFormChange({
      [key]: checked,
    });
  };

  const handleOtherDescriptionChange = (description: string) => {
    onFormChange({
      isOtherDescription: description,
    });
  };

  return (
    <div className={styles.root}>
      {/* Header */}
      <div className={styles.header}>
        <h2 id={headingId} className={styles.title}>Nature of Crimes<RequiredIndicator /></h2>
        <p id={headingDescId} className={styles.muted}>
          Nature of Crimes Being Investigated
        </p>
      </div>

      {/* Child Exploitation Yes/No (moved here from Type of Request step). When
          Yes, the crime checkboxes below are not required. */}
      <Card>
        <CardHeader>
          <CardTitle id={cpConcernLabelId} className={styles.title}>Child Exploitation Related<RequiredIndicator /></CardTitle>
          <CardDescription id={cpConcernDescId}>Is this request related to child exploitation or abuse?</CardDescription>
        </CardHeader>
        <CardContent>
          <div data-field="cpConcern">
            <RadioGroup
              aria-labelledby={cpConcernLabelId}
              aria-describedby={cpConcernDescId}
              value={cpConcern === null ? '' : cpConcern.toString()}
              onValueChange={(value) => onCpConcernChange(value === 'true')}
              items={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
            />
          </div>
          {errors.cpConcern && <p className={styles.errors}>{errors.cpConcern}</p>}
        </CardContent>
      </Card>

      {/* Crime Types Selection */}
      <Card>
        <CardContent>
          <div data-field="natureOfCrime" className={styles.grid} role="group" aria-labelledby={headingId} aria-describedby={headingDescId}>
            {CRIME_TYPE_OPTIONS.map((crime) => (
              <div key={crime.key} className={styles.option}>
                <Checkbox
                  id={crime.key}
                  checked={formData[crime.key] === true}
                  onCheckedChange={(checked) => handleCrimeTypeChange(crime.key, !!checked)}
                />
                <div className={styles.optionContent}>
                  <Label htmlFor={crime.key} className={styles.optionLabel}>
                    {crime.label}
                  </Label>
                  <p className={styles.hint}>
                    {crime.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {errors.natureOfCrime && (
            <p className={styles.errors}>{errors.natureOfCrime}</p>
          )}
        </CardContent>
      </Card>

      {/* Other Description */}
      {formData.isOther && (
        <Card>
          <CardHeader>
            <CardTitle>Other Crime Description</CardTitle>
            <CardDescription>
              Please provide a detailed description of the crime type not listed above
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className={styles.textareaContainer}>
              <Label htmlFor="otherDescription">Description<RequiredIndicator /></Label>
              <Textarea
                id="otherDescription"
                value={formData.isOtherDescription || ''}
                onChange={(e) => handleOtherDescriptionChange(e.target.value)}
                placeholder="Describe the nature of the crime..."
                rows={4}
                maxLength={FIELD_LIMITS.OTHER_DESCRIPTION}
              />
              <div className={`${styles.hint} ${styles.right}`}>
                {(formData.isOtherDescription?.length || 0)}/{FIELD_LIMITS.OTHER_DESCRIPTION} characters
              </div>
              {errors.isOtherDescription && (
                <p className={styles.errors}>{errors.isOtherDescription}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
};
